import React, { useState, useMemo } from "react";
import { useNavigate } from "react-router-dom";
import { Calculator, PlusCircle, ChevronDown, ChevronUp } from "lucide-react";
import { PieChart, Pie, Cell, Tooltip, ResponsiveContainer } from "recharts";
import { useTheme } from "../context/ThemeContext";
import { usePermissions } from "../context/PermissionContext";

// Spec ranges
const LIMITS = {
  amount: { min: 10000, max: 5000000, step: 10000 },
  rate: { min: 8, max: 36, step: 0.25 },
  tenure: { min: 1, max: 84, step: 1 },
};

const formatINR = (val) => {
  if (val === undefined || val === null || isNaN(val)) return "₹0";
  return new Intl.NumberFormat("en-IN", {
    style: "currency",
    currency: "INR",
    maximumFractionDigits: 0,
  }).format(val);
};

const clamp = (val, { min, max }) => Math.min(max, Math.max(min, Number(val) || min));

/**
 * Reducing balance EMI: P*R*(1+R)^N / ((1+R)^N - 1)
 * Frontend pe hi calculate — backend call ki zaroorat nahi (instant slider feedback).
 */
const buildSchedule = (principal, annualRate, months) => {
  const R = annualRate / 12 / 100;
  const emi = R > 0
    ? (principal * R * Math.pow(1 + R, months)) / (Math.pow(1 + R, months) - 1)
    : principal / months;

  const rows = [];
  let balance = principal;
  for (let month = 1; month <= months; month++) {
    const interest = balance * R;
    // Last month me rounding ka bacha hua balance clear karo
    const principalPart = month === months ? balance : emi - interest;
    balance = Math.max(0, balance - principalPart);
    rows.push({ month, emi: principalPart + interest, principal: principalPart, interest, balance });
  }
  const totalPayable = rows.reduce((sum, r) => sum + r.emi, 0);
  return { emi, rows, totalPayable, totalInterest: totalPayable - principal };
};

const SliderField = ({ label, value, display, limits, onChange, color, suffix }) => (
  <div>
    <div className="flex justify-between items-center mb-1">
      <label className="text-xs font-bold text-gray-700">{label}</label>
      <span className="text-sm font-black" style={{ color }}>{display}</span>
    </div>
    <input
      type="range"
      min={limits.min}
      max={limits.max}
      step={limits.step}
      value={value}
      onChange={(e) => onChange(Number(e.target.value))}
      className="w-full cursor-pointer"
      style={{ accentColor: color }}
    />
    <div className="flex items-center gap-2 mt-2">
      <input
        type="number"
        min={limits.min}
        max={limits.max}
        step={limits.step}
        value={value}
        onChange={(e) => onChange(e.target.value === "" ? limits.min : Number(e.target.value))}
        onBlur={(e) => onChange(clamp(e.target.value, limits))}
        className="w-full px-3 py-1.5 text-sm bg-gray-50 rounded-xl border border-gray-200 focus:outline-none"
      />
      <span className="text-xs text-gray-500 whitespace-nowrap">{suffix}</span>
    </div>
    <div className="flex justify-between text-[10px] text-gray-400 mt-1">
      <span>{limits.min.toLocaleString("en-IN")}</span>
      <span>{limits.max.toLocaleString("en-IN")}</span>
    </div>
  </div>
);

export default function LoanCalculator() {
  const { currentTheme } = useTheme();
  const { has } = usePermissions();
  const navigate = useNavigate();
  const primaryColor = currentTheme?.color || "#2e856e";

  const [amount, setAmount] = useState(500000);
  const [rate, setRate] = useState(12);
  const [tenure, setTenure] = useState(24);
  const [feePercent, setFeePercent] = useState(1.5);
  const [showAll, setShowAll] = useState(false);

  // Typing ke beech invalid value aaye to bhi calculation valid range pe hi ho
  const safeAmount = clamp(amount, LIMITS.amount);
  const safeRate = clamp(rate, LIMITS.rate);
  const safeTenure = Math.round(clamp(tenure, LIMITS.tenure));

  const { emi, rows, totalPayable, totalInterest } = useMemo(
    () => buildSchedule(safeAmount, safeRate, safeTenure),
    [safeAmount, safeRate, safeTenure]
  );
  const processingFee = (safeAmount * (Number(feePercent) || 0)) / 100;

  const pieData = [
    { name: "Principal", value: safeAmount },
    { name: "Total Interest", value: Math.max(0, totalInterest) },
  ];
  const pieColors = [primaryColor, "#f59e0b"];
  const visibleRows = showAll ? rows : rows.slice(0, 12);

  return (
    <div className="w-full space-y-6 pb-16">
      {/* ── Page Header ── */}
      <div className="flex flex-col md:flex-row md:items-center justify-between gap-4 bg-white p-5 rounded-2xl border border-[#e5e7eb] shadow-sm">
        <div className="flex items-center gap-3">
          <div
            className="w-10 h-10 rounded-xl flex items-center justify-center text-white shadow-md"
            style={{ backgroundColor: primaryColor }}
          >
            <Calculator className="w-5 h-5" />
          </div>
          <div>
            <h1 className="text-xl md:text-2xl font-bold text-gray-900">Loan EMI Calculator</h1>
            <p className="text-xs md:text-sm text-gray-500">
              Reducing balance EMI, interest split and month-wise amortization
            </p>
          </div>
        </div>

        {has("LOAN_CREATE") && (
          <button
            onClick={() =>
              navigate("/fintech/loans/new", {
                state: { prefill: { amount: safeAmount, tenureMonths: safeTenure, interestRate: safeRate } },
              })
            }
            className="flex items-center gap-2 px-4 py-2.5 text-white text-sm font-semibold rounded-xl shadow-md transition-all hover:brightness-105 active:scale-95"
            style={{ backgroundColor: primaryColor }}
          >
            <PlusCircle className="w-4 h-4" />
            <span>Start Application with these terms</span>
          </button>
        )}
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-12 gap-6">
        {/* ── Inputs ── */}
        <div className="lg:col-span-5 bg-white p-6 rounded-2xl border border-[#e5e7eb] shadow-sm space-y-6">
          <SliderField
            label="Loan Amount"
            value={amount}
            display={formatINR(safeAmount)}
            limits={LIMITS.amount}
            onChange={setAmount}
            color={primaryColor}
            suffix="₹"
          />
          <SliderField
            label="Interest Rate (p.a.)"
            value={rate}
            display={`${safeRate}%`}
            limits={LIMITS.rate}
            onChange={setRate}
            color={primaryColor}
            suffix="%"
          />
          <SliderField
            label="Tenure"
            value={tenure}
            display={`${safeTenure} Months`}
            limits={LIMITS.tenure}
            onChange={setTenure}
            color={primaryColor}
            suffix="months"
          />
          <div>
            <label className="block text-xs font-bold text-gray-700 mb-1">Processing Fee (%)</label>
            <input
              type="number"
              min={0}
              max={10}
              step={0.1}
              value={feePercent}
              onChange={(e) => setFeePercent(e.target.value)}
              className="w-full px-3 py-1.5 text-sm bg-gray-50 rounded-xl border border-gray-200 focus:outline-none"
            />
          </div>
        </div>

        {/* ── Results ── */}
        <div className="lg:col-span-7 space-y-6">
          <div className="grid grid-cols-2 gap-4">
            <div className="col-span-2 p-6 rounded-2xl border border-emerald-200/80 shadow-sm bg-gradient-to-br from-emerald-50/70 to-white">
              <span className="text-xs font-bold uppercase tracking-wider" style={{ color: primaryColor }}>
                Monthly EMI
              </span>
              <div className="text-4xl font-black text-gray-900 mt-1">
                {formatINR(emi)} <span className="text-sm font-medium text-gray-500">/ month</span>
              </div>
            </div>
            <div className="p-5 bg-white rounded-2xl border border-gray-200 shadow-sm">
              <span className="text-xs font-bold uppercase text-gray-400">Total Interest</span>
              <div className="text-xl font-black text-amber-600 mt-1">{formatINR(totalInterest)}</div>
            </div>
            <div className="p-5 bg-white rounded-2xl border border-gray-200 shadow-sm">
              <span className="text-xs font-bold uppercase text-gray-400">Total Payable</span>
              <div className="text-xl font-black text-gray-900 mt-1">{formatINR(totalPayable)}</div>
            </div>
            <div className="p-5 bg-white rounded-2xl border border-gray-200 shadow-sm">
              <span className="text-xs font-bold uppercase text-gray-400">Processing Fee</span>
              <div className="text-xl font-black text-gray-900 mt-1">{formatINR(processingFee)}</div>
            </div>
            <div className="p-5 bg-white rounded-2xl border border-gray-200 shadow-sm">
              <span className="text-xs font-bold uppercase text-gray-400">Net Disbursal</span>
              <div className="text-xl font-black text-gray-900 mt-1">{formatINR(safeAmount - processingFee)}</div>
              <p className="text-[10px] text-gray-400 mt-0.5">Amount minus processing fee</p>
            </div>
          </div>

          <div className="bg-white p-6 rounded-2xl border border-[#e5e7eb] shadow-sm">
            <h2 className="text-base font-bold text-gray-900 mb-2">Principal vs Interest</h2>
            <div className="flex flex-col sm:flex-row items-center gap-4">
              <div className="h-48 w-full sm:w-1/2">
                <ResponsiveContainer width="100%" height="100%">
                  <PieChart>
                    <Pie data={pieData} dataKey="value" innerRadius={50} outerRadius={75} paddingAngle={3}>
                      {pieData.map((entry, idx) => (
                        <Cell key={entry.name} fill={pieColors[idx]} />
                      ))}
                    </Pie>
                    <Tooltip formatter={(v) => formatINR(v)} />
                  </PieChart>
                </ResponsiveContainer>
              </div>
              <div className="space-y-2 text-sm w-full sm:w-1/2">
                {pieData.map((d, idx) => (
                  <div key={d.name} className="flex items-center justify-between">
                    <span className="flex items-center gap-2 text-gray-700">
                      <span className="w-3 h-3 rounded-full" style={{ backgroundColor: pieColors[idx] }} />
                      {d.name}
                    </span>
                    <span className="font-bold text-gray-900">
                      {formatINR(d.value)}{" "}
                      <span className="text-xs text-gray-400">
                        ({totalPayable > 0 ? ((d.value / totalPayable) * 100).toFixed(1) : 0}%)
                      </span>
                    </span>
                  </div>
                ))}
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* ── Amortization Table ── */}
      <div className="bg-white rounded-2xl border border-[#e5e7eb] shadow-sm overflow-hidden p-6">
        <div className="flex items-center justify-between mb-4">
          <div>
            <h2 className="text-base font-bold text-gray-900">Amortization Schedule</h2>
            <p className="text-xs text-gray-500">
              {showAll ? `All ${rows.length} months` : `First ${visibleRows.length} of ${rows.length} months`}
            </p>
          </div>
          {rows.length > 12 && (
            <button
              onClick={() => setShowAll((s) => !s)}
              className="text-xs font-bold flex items-center gap-1 hover:underline"
              style={{ color: primaryColor }}
            >
              {showAll ? <>Show first 12 <ChevronUp className="w-3.5 h-3.5" /></> : <>Show all months <ChevronDown className="w-3.5 h-3.5" /></>}
            </button>
          )}
        </div>
        <div className="overflow-x-auto">
          <table className="w-full text-left text-sm border-collapse">
            <thead>
              <tr className="border-b border-gray-200 bg-gray-50/75 text-gray-600 text-xs uppercase font-bold">
                <th className="py-3 px-4">Month</th>
                <th className="py-3 px-4 text-right">EMI</th>
                <th className="py-3 px-4 text-right">Principal</th>
                <th className="py-3 px-4 text-right">Interest</th>
                <th className="py-3 px-4 text-right">Balance</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-100">
              {visibleRows.map((r) => (
                <tr key={r.month} className="hover:bg-gray-50/50 text-xs">
                  <td className="py-2.5 px-4 font-bold text-gray-900">{r.month}</td>
                  <td className="py-2.5 px-4 text-right font-semibold text-gray-900">{formatINR(r.emi)}</td>
                  <td className="py-2.5 px-4 text-right text-gray-600">{formatINR(r.principal)}</td>
                  <td className="py-2.5 px-4 text-right text-amber-700">{formatINR(r.interest)}</td>
                  <td className="py-2.5 px-4 text-right font-bold text-gray-700">{formatINR(r.balance)}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
}
